import { useEffect, useMemo, useState } from "react";
import { Link, Redirect } from "wouter";
import type { TenantTransactionsFeedResponse } from "@orbit/api-zod";
import { getMerchantToken, merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";

/**
 * Merchant Transactions list — Phase 11.
 *
 * Reads `/api/v1/tenant/transactions/feed`. Filters:
 *   • status — multi-select chips (COMPLETED / VOIDED / PARTIAL_REFUND
 *     / REFUNDED). No selection = show all.
 *   • date range — two `<input type="date">` fields, both optional.
 *   • customer search — single text box; 4 digits → phone last-4,
 *     anything else → name prefix.
 *
 * Pagination is cursor-based (server emits `nextCursor`); the page
 * tracks a stack of cursors so "Older" appends and "Newer" pops.
 *
 * Filter changes always reset pagination — otherwise a stale cursor
 * from a previous filter could resurface rows the new filter excludes.
 */

const STATUSES = [
  "COMPLETED",
  "VOIDED",
  "PARTIAL_REFUND",
  "REFUNDED",
] as const;
type Status = (typeof STATUSES)[number];

function statusBadgeClass(status: Status): string {
  switch (status) {
    case "COMPLETED":
      return "bg-green-100 text-green-800";
    case "VOIDED":
      return "bg-stone-200 text-stone-700";
    case "PARTIAL_REFUND":
      return "bg-amber-100 text-amber-800";
    case "REFUNDED":
      return "bg-red-100 text-red-800";
  }
}

export function MerchantTransactionsPage() {
  if (!getMerchantToken()) return <Redirect to="/merchant/login" />;

  const [statuses, setStatuses] = useState<Set<Status>>(new Set());
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  const [data, setData] = useState<TenantTransactionsFeedResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Cursor stack: each entry is the cursor used to fetch that page.
  // Index 0 (no cursor) means "first page".
  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);

  const queryString = useMemo(() => {
    const sp = new URLSearchParams();
    sp.set("limit", "25");
    for (const s of statuses) sp.append("status", s);
    if (from) sp.set("from", new Date(`${from}T00:00:00Z`).toISOString());
    if (to) sp.set("to", new Date(`${to}T23:59:59.999Z`).toISOString());
    const trimmed = search.trim();
    if (trimmed) sp.set("customerSearch", trimmed);
    const cursor = cursorStack[cursorStack.length - 1];
    if (cursor) sp.set("cursor", cursor);
    return sp.toString();
  }, [statuses, from, to, search, cursorStack]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    void (async () => {
      const r = await merchantFetch<TenantTransactionsFeedResponse>(
        `/api/v1/tenant/transactions/feed?${queryString}`,
      );
      if (cancelled) return;
      setLoading(false);
      if (!r.ok) {
        setErr(`${r.code}: ${r.message}`);
        setData(null);
        return;
      }
      setData(r.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [queryString]);

  function toggleStatus(s: Status) {
    const next = new Set(statuses);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setStatuses(next);
    setCursorStack([null]);
  }

  function resetFilters() {
    setStatuses(new Set());
    setFrom("");
    setTo("");
    setSearch("");
    setCursorStack([null]);
  }

  function nextPage() {
    if (data?.nextCursor) setCursorStack((s) => [...s, data.nextCursor]);
  }

  function prevPage() {
    if (cursorStack.length > 1) setCursorStack((s) => s.slice(0, -1));
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="mx-auto max-w-2xl px-6 pt-8">
        <MerchantNav current="transactions" />

        <h1 className="mt-6 text-2xl font-bold text-navy">Transactions</h1>
        <p className="mt-1 text-sm text-ink-dark">
          Recent scans, voids, and refunds. Filter to drill in.
        </p>

        <section className="mt-5 card space-y-3">
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((s) => {
              const active = statuses.has(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatus(s)}
                  className={`rounded-pill px-3 py-1 text-xs font-semibold ${
                    active
                      ? "bg-navy text-white"
                      : "bg-stone-100 text-ink-dark hover:bg-stone-200"
                  }`}
                >
                  {s.replace("_", " ")}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-ink-dark">
                From
              </span>
              <input
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setCursorStack([null]);
                }}
                className="mt-1 input"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-ink-dark">
                To
              </span>
              <input
                type="date"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setCursorStack([null]);
                }}
                className="mt-1 input"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-wider text-ink-dark">
              Customer (name prefix or phone last-4)
            </span>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCursorStack([null]);
              }}
              placeholder="e.g. Maria or 1234"
              className="mt-1 input"
            />
          </label>

          {(statuses.size > 0 || from || to || search) && (
            <button
              type="button"
              onClick={resetFilters}
              className="text-xs text-navy underline"
            >
              Clear filters
            </button>
          )}
        </section>

        {err && (
          <div className="mt-4 rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {err}
          </div>
        )}

        <section className="mt-5">
          {loading && !data && (
            <p className="text-sm text-ink-dark">Loading…</p>
          )}
          {data && data.items.length === 0 && (
            <p className="text-sm text-ink-dark">
              No transactions match these filters.
            </p>
          )}
          <ul className="space-y-2">
            {data?.items.map((row) => (
              <li key={row.id}>
                <Link
                  href={`/merchant/transactions/${row.id}`}
                  className="block card hover:border-navy transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-navy">
                        {row.customerName}
                      </p>
                      <p className="text-xs text-ink-dark">
                        ··· {row.customerPhoneLast4} ·{" "}
                        {new Date(row.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm text-navy">
                        {row.amount ? `₱${row.amount}` : "—"}
                      </p>
                      <span
                        className={`mt-1 inline-block rounded-pill px-2 py-0.5 text-[10px] font-semibold uppercase ${statusBadgeClass(
                          row.status,
                        )}`}
                      >
                        {row.status.replace("_", " ")}
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          {data && (data.nextCursor || cursorStack.length > 1) && (
            <div className="mt-4 flex justify-between">
              <button
                type="button"
                disabled={cursorStack.length <= 1}
                onClick={prevPage}
                className="btn-secondary disabled:opacity-50"
              >
                Newer
              </button>
              <button
                type="button"
                disabled={!data.nextCursor}
                onClick={nextPage}
                className="btn-secondary disabled:opacity-50"
              >
                Older
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
